// Quittance creation shared by the web app (src/lib/quittances.ts) and the MCP server.
import { getLeaseLines, leaseLineTotals } from './lease-lines.ts';
import { computeIndexation, indexationInputForLease, isIndexationDue, renderAnnexHtml, round2 } from './indexation.ts';

export interface QuittanceLease {
  id: string;
  rent_amount: number | null;
  charges_amount: number | null;
  vat_rate: number | null;
  rent_lines?: unknown;
  payment_day: number | null;
  organization_id: string;
  // Annual indexation (see indexation.ts); optional so existing selects keep working.
  [key: string]: unknown;
  properties: { scis: { id: string } | null } | null;
}

export type CreateQuittanceResult = { status: 'created' | 'skipped'; reason?: string };

// Creates a draft quittance for one lease + period: ensures the rent period,
// records the payment (marks it paid) and issues a receipt awaiting validation.
// rent_due and charges_due are stored TTC (the contract's TVA applies to both),
// matching the quittance PDF/email and the SUPER PDP invoice builder.
// deno-lint-ignore no-explicit-any
export async function createQuittanceForPeriod(sb: any, lease: QuittanceLease, period: string, paymentDate: string, method: string): Promise<CreateQuittanceResult> {
  const sciId = lease.properties?.scis?.id;
  if (!sciId) throw new Error('SCI introuvable pour ce contrat');
  const orgId = lease.organization_id;

  const rentHT = Number(lease.rent_amount) || 0;
  const chargesHT = Number(lease.charges_amount) || 0;
  const vatRate = Number(lease.vat_rate) || 0;
  let rentTTC = rentHT * (1 + vatRate / 100);
  let chargesDue = chargesHT * (1 + vatRate / 100);
  // Multi-line contracts: each line's TVA applies to its loyer and charges.
  const lines = getLeaseLines(lease);
  if (lines) {
    const t = leaseLineTotals(lines);
    rentTTC = t.rentTtc;
    chargesDue = t.chargesTtc;
  }
  let totalDue = rentTTC + chargesDue;

  // Annual indexation: the revision period gets detailed lines (old rent, new rent,
  // deposit adjustment). Blocked if the index for year N has not been entered yet.
  let indexation: { input: ReturnType<typeof indexationInputForLease>; result: ReturnType<typeof computeIndexation> } | null = null;
  if (isIndexationDue(lease, period)) {
    const code = String(lease.indexation_index || 'ICC');
    const quarter = Number(lease.indexation_quarter) || 2;
    const idxYear = Number(period.slice(0, 4));
    const { data: iv, error: ivErr } = await sb.from('index_values').select('value')
      .eq('index_code', code).eq('year', idxYear).eq('quarter', quarter).maybeSingle();
    if (ivErr) throw ivErr;
    if (!iv) throw new Error(`Indexation annuelle : indice ${code} T${quarter} ${idxYear} non saisi, quittance non générée`);
    const input = indexationInputForLease(lease, period, Number(iv.value));
    const result = computeIndexation(input);
    indexation = { input, result };
    rentTTC = result.totals.rentTtc;
    chargesDue = result.totals.chargesTtc;
    totalDue = result.totals.toPay;
  }

  const [year, month] = period.split('-');
  const dueDate = `${year}-${month}-${String(lease.payment_day || 5).padStart(2, '0')}`;

  // 1. Ensure the rent period exists.
  const { data: existingPeriod, error: rpErr } = await sb
    .from('rent_periods')
    .select('*')
    .eq('lease_id', lease.id)
    .eq('period_yyyymm', period)
    .maybeSingle();
  if (rpErr) throw rpErr;

  let rp = existingPeriod;
  if (!rp) {
    const { data: created, error } = await sb
      .from('rent_periods')
      .insert({
        organization_id: orgId,
        lease_id: lease.id,
        period_yyyymm: period,
        rent_due: rentTTC,
        charges_due: chargesDue,
        total_due: totalDue,
        due_date: dueDate,
        status: 'pending',
        total_paid: 0,
        balance: totalDue,
        invoice_lines: indexation?.result.lines ?? null,
      })
      .select('*')
      .single();
    if (error) throw error;
    rp = created;
  } else if (indexation && !rp.invoice_lines && Number(rp.total_paid) === 0) {
    // Period created earlier without the revision: apply it before issuing the quittance.
    const { data: updated, error } = await sb
      .from('rent_periods')
      .update({ rent_due: rentTTC, charges_due: chargesDue, total_due: totalDue, balance: totalDue, invoice_lines: indexation.result.lines })
      .eq('id', rp.id)
      .select('*')
      .single();
    if (error) throw error;
    rp = updated;
  }
  if (!rp) throw new Error('Période de loyer introuvable');

  // One quittance per period.
  const { data: existingReceipt } = await sb
    .from('receipts')
    .select('id')
    .eq('rent_period_id', rp.id)
    .maybeSingle();
  if (existingReceipt) return { status: 'skipped', reason: 'Quittance déjà existante' };

  const remaining = Number(rp.total_due) - Number(rp.total_paid);
  const amount = remaining > 0 ? remaining : Number(rp.total_due);

  // 2. Payment + allocation, then mark the period paid.
  const { data: payment, error: payErr } = await sb
    .from('payments')
    .insert({
      organization_id: orgId,
      lease_id: lease.id,
      amount,
      payment_date: paymentDate,
      method,
      notes: `Quittance ${period}`,
    })
    .select('id')
    .single();
  if (payErr) throw payErr;

  try {
    const { error: allocErr } = await sb.from('payment_allocations').insert({
      organization_id: orgId,
      payment_id: payment.id,
      rent_period_id: rp.id,
      amount,
    });
    if (allocErr) throw allocErr;

    const { error: periodErr } = await sb
      .from('rent_periods')
      .update({ total_paid: Number(rp.total_paid) + amount, balance: 0, status: 'paid' })
      .eq('id', rp.id);
    if (periodErr) throw periodErr;

    // 3. Receipt (draft — awaits validation before send + PDP).
    const { data: receiptNumber, error: numErr } = await sb.rpc('get_next_document_number', {
      p_sci_id: sciId,
      p_document_type: 'receipt',
    });
    if (numErr || !receiptNumber) throw new Error('Erreur lors de la génération du numéro de quittance');

    const { error: recErr } = await sb.from('receipts').insert({
      organization_id: orgId,
      rent_period_id: rp.id,
      receipt_number: receiptNumber as string,
      email_status: 'not_sent',
      validation_status: 'draft',
    });
    if (recErr) throw recErr;
  } catch (e) {
    await sb.from('payments').delete().eq('id', payment.id);
    throw e;
  }

  if (indexation && rp.invoice_lines) await recordIndexation(sb, lease, rp.id, indexation);
  return { status: 'created' };
}

// Saves the revision history + annex, links it to the period and moves the lease to the new rent.
// deno-lint-ignore no-explicit-any
async function recordIndexation(sb: any, lease: QuittanceLease, rentPeriodId: string, ix: { input: ReturnType<typeof indexationInputForLease>; result: ReturnType<typeof computeIndexation> }) {
  const { input, result } = ix;
  const { data: info } = await sb.from('leases')
    .select('properties(name, address, scis(name)), tenants(company_name, first_name, last_name)')
    .eq('id', lease.id).single();
  const t = info?.tenants;
  const annexHtml = renderAnnexHtml({
    sciName: info?.properties?.scis?.name ?? '',
    tenantName: t?.company_name || `${t?.first_name ?? ''} ${t?.last_name ?? ''}`.trim(),
    propertyLabel: [info?.properties?.name, info?.properties?.address].filter(Boolean).join(' — '),
    input, result,
  });

  const { data: hist, error: he } = await sb.from('lease_indexations').upsert({
    organization_id: lease.organization_id,
    lease_id: lease.id,
    year: input.year,
    index_code: input.indexCode,
    index_quarter: input.indexQuarter,
    base_year: input.baseYear,
    base_value: input.baseValue,
    new_value: input.newValue,
    old_monthly_rent: input.oldMonthlyRent,
    new_monthly_rent: result.newMonthlyRent,
    old_deposit: input.currentDeposit,
    new_deposit: result.newDeposit,
    deposit_adjustment: result.depositAdjustment,
    rent_period_id: rentPeriodId,
    annex_html: annexHtml,
  }, { onConflict: 'lease_id,year' }).select('id').single();
  if (he) throw new Error(`Quittance créée mais historique d'indexation non enregistré : ${he.message}`);

  await sb.from('rent_periods').update({ indexation_id: hist.id }).eq('id', rentPeriodId);

  const { error: ue } = await sb.from('leases').update({
    monthly_rent: result.newMonthlyRent,
    rent_amount: round2(result.newMonthlyRent * input.monthsInCall),
    deposit_amount: result.newDeposit,
    indexation_base_year: input.year,
    indexation_base_value: input.newValue,
    last_indexation_date: new Date().toISOString().slice(0, 10),
  }).eq('id', lease.id);
  if (ue) throw new Error(`Quittance créée mais contrat non mis à jour après indexation : ${ue.message}`);
}
